import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronRight, InfoIcon } from 'lucide-react';
import React from 'react';

import { CopyToClipboard } from '@/components/CopyToClipboard';
import { MarkdownPreview } from '@/components/markdown/MarkdownPreview';
import { Tooltip as InfoTooltip } from '@/components/Tooltip';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from '@/components/ui/accordion';
import { cn } from '@/utils/classname';

import type {
  Proof,
  ProofBanner,
  ProofCodeBlock,
  ProofCodeSnippet,
  ProofElement,
  ProofGroup,
  ProofHeading,
  ProofHttpExchange,
  ProofImage,
  ProofKeyValue,
  ProofKeyValueRow,
  ProofList,
  ProofMetric,
  ProofParagraph,
  ProofReference,
  ProofSection,
  ProofSeverity,
  ProofTable,
  ProofTimeline,
} from './types';
import { useResolvedImageSrc } from './useResolvedImageSrc';

// Severity → token class for borders, backgrounds, and text tints.
// Mirrors the legacy POE.tsx severity color choices for visual consistency.
const SEVERITY_BORDER: Record<ProofSeverity, string> = {
  critical: 'border-chariot-stroke-critical',
  high: 'border-chariot-stroke-high',
  medium: 'border-chariot-stroke-medium',
  low: 'border-chariot-stroke-low',
  info: 'border-chariot-stroke-info',
  success: 'border-chariot-stroke-info',
};

const SEVERITY_BG: Record<ProofSeverity, string> = {
  critical: 'bg-chariot-status-critical/10',
  high: 'bg-chariot-status-high/10',
  medium: 'bg-chariot-status-medium/10',
  low: 'bg-chariot-status-low/10',
  info: 'bg-chariot-status-info/10',
  success: 'bg-chariot-status-info/10',
};

const SEVERITY_TEXT: Record<ProofSeverity, string> = {
  critical: 'text-chariot-stroke-critical',
  high: 'text-chariot-text-orange',
  medium: 'text-chariot-text-yellow',
  low: 'text-chariot-text-secondary',
  info: 'text-chariot-stroke-info',
  success: 'text-chariot-stroke-info',
};

// `layout` selects the structured-proof rendering variant:
//   • undefined / 'default' → the existing nested-card output (section card →
//     heading sub-cards → code cards). Byte-for-byte unchanged for all 16
//     processors that mount ProofRenderer through usePOESections; do NOT alter.
//   • 'accordion' → the flattened Evidence-tab variant: one outer frame with
//     accordion rows separated by dividers, no per-step bordered card. Opt-in
//     only from the Evidence/Information path in usePOESections.
type ProofLayout = 'default' | 'accordion';

interface ProofRendererProps {
  proof: Proof;
  className?: string;
  // bare = suppress section chrome. Use when an outer container (e.g.
  // POE.tsx's sub-tab card) already provides the card frame. In the default
  // layout this drops the border + header strip; in the accordion layout it
  // hides only the redundant header-strip title (the frame, step count, and
  // Expand/Collapse-all control are kept). The default `false` is appropriate
  // for standalone mounts (tests, embedded previews).
  bare?: boolean;
  layout?: ProofLayout;
}

// ProofRenderer dispatches a v1 structured proof into per-element components.
// Visual conventions follow the legacy POE.tsx renderers so structured and
// legacy proof bodies look the same in the drawer.
export function ProofRenderer({
  proof,
  className,
  bare,
  layout,
}: ProofRendererProps) {
  // Defensive `?? []` here and at every other iteration point: Go marshals
  // nil slices as `null`, and older POE bodies still in S3 (pre-#5694) ship
  // sections with `"elements": null`. Iterating null with `for...of` or
  // `.map` throws "TypeError: a is not iterable" and the React Router error
  // boundary blows away the whole drawer. See proof_builder.go normalizeElements.
  const sections = proof.sections ?? [];

  // Accordion variant: render the whole proof (all sections' heading-led
  // groups) as flattened accordion rows inside a single outer frame.
  if (layout === 'accordion') {
    return (
      <AccordionSectionView
        sections={sections}
        className={className}
        bare={bare}
      />
    );
  }

  return (
    <div className={cn('flex flex-col gap-6', className)}>
      {sections.map((section, i) => (
        <SectionView key={i} section={section} bare={bare ?? false} />
      ))}
    </div>
  );
}

// SectionView either:
//   • renders only the elements when `bare` (POE.tsx provides card chrome)
//   • or renders a self-contained card with header strip otherwise
function SectionView({
  section,
  bare,
}: {
  section: ProofSection;
  bare: boolean;
}) {
  const elements = section.elements ?? [];
  if (bare || !section.title) {
    return <ElementList elements={elements} />;
  }
  return (
    <section className="border-chariot-stroke bg-chariot-background-secondary overflow-hidden rounded-lg border shadow-sm">
      <div className="border-chariot-stroke border-b px-4 py-2">
        <h3 className="text-chariot-text-secondary text-xs font-medium tracking-wider uppercase">
          {section.title}
        </h3>
      </div>
      <div className="px-4 py-3">
        <ElementList elements={elements} />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Accordion (flattened) variant — opt-in via `layout="accordion"`.
//
// Replaces the section → step-card → code-card nesting with ONE outer frame
// whose accordion rows (the heading-led groups, i.e. the chain's steps) are
// separated by `border-t` dividers. No per-step bordered card.
//
// DOM-unmount tradeoff: Radix `AccordionContent` UNMOUNTS collapsed panels,
// so browser find-in-page (Ctrl-F) and copy do NOT reach a collapsed step's
// text. Per design-spec §4.2 this is the accepted shared-primitive behavior;
// the mitigation is the always-present "Expand all" control in the header
// strip (plus Step 1 — and all steps when ≤2 total — open by default).
// ---------------------------------------------------------------------------

type ProofStep = { title?: string; elements: ProofElement[] };

// The backend titles each chain step `"Step N — <action>"` (proof.go addSteps),
// but the row already renders its own "STEP N" eyebrow + crimson number badge.
// Strip a leading `Step <n>` followed by `—`, `-`, or `:` (and surrounding
// spaces), case-insensitively, so the row reads `STEP 1 / <action>` instead of
// duplicating `Step 1 —`. Returns undefined when stripping leaves nothing (the
// eyebrow + badge already identify the step).
function cleanStepTitle(title: string | undefined): string | undefined {
  if (!title) return undefined;
  const stripped = title.replace(/^\s*step\s+\d+\s*[—\-:]?\s*/i, '').trim();
  return stripped === '' ? undefined : stripped;
}

// Flatten every section's top-level `group` elements into a single ordered step
// list. Each `group` element becomes a step (title = group.title, elements =
// group.elements). Any non-group element (or any element before the first
// group) is collected as `intro` and rendered above the rows rather than as a
// numbered step. See proof.go: the "Detection Claims" section is a run of
// `group` elements (the real steps); the "Context" section is `heading`
// elements (short prose) that fall through to `intro` → the steps-empty
// fallback renders them flat.
function buildProofSteps(sections: ProofSection[]): {
  intro: ProofElement[];
  steps: ProofStep[];
  title?: string;
} {
  const intro: ProofElement[] = [];
  const steps: ProofStep[] = [];
  let title: string | undefined;

  for (const section of sections ?? []) {
    if (section.title && !title) title = section.title;
    for (const el of section.elements ?? []) {
      if (el.type === 'group' && el.group) {
        steps.push({
          title: el.group.title,
          elements: el.group.elements ?? [],
        });
      } else {
        intro.push(el);
      }
    }
  }

  return { intro, steps, title };
}

function AccordionSectionView({
  sections,
  className,
  bare,
}: {
  sections: ProofSection[];
  className?: string;
  // bare = the outer surface (e.g. the POE sub-tab whose label is already the
  // section title) provides the title context, so hide the header-strip title.
  bare?: boolean;
}) {
  const { intro, steps, title } = buildProofSteps(sections);

  // No headings anywhere → there are no "steps" to flatten. Fall back to the
  // default bare rendering so non-step proofs still display correctly.
  if (steps.length === 0) {
    return (
      <div className={cn('flex flex-col gap-6', className)}>
        {sections.map((section, i) => (
          <SectionView key={i} section={section} bare />
        ))}
      </div>
    );
  }

  const stepValues = steps.map((_, i) => `step-${i}`);
  // Step 1 open by default; ALL open when the chain has ≤2 steps (the common
  // case shouldn't hide anything). 3+ steps collapse the tail.
  const defaultOpen = steps.length <= 2 ? stepValues : [stepValues[0]];

  return (
    <div
      className={cn(
        'border-chariot-stroke bg-chariot-background-primary overflow-hidden rounded-md border',
        className
      )}
    >
      <AccordionStepList
        title={title || 'Detection Claims'}
        hideTitle={bare}
        intro={intro}
        steps={steps}
        stepValues={stepValues}
        defaultOpen={defaultOpen}
      />
    </div>
  );
}

// AccordionStepList owns the accordion's open-value state so the
// Expand-all / Collapse-all control in the header strip can drive it.
// `type="multiple"` lets any subset of steps be open at once.
function AccordionStepList({
  title,
  hideTitle,
  intro,
  steps,
  stepValues,
  defaultOpen,
}: {
  title: string;
  // hideTitle = suppress the InfoIcon + <h3> title cluster (the step count and
  // Expand/Collapse-all control always render; `ml-auto` keeps it right-aligned).
  hideTitle?: boolean;
  intro: ProofElement[];
  steps: ProofStep[];
  stepValues: string[];
  defaultOpen: string[];
}) {
  const [openValues, setOpenValues] = React.useState<string[]>(defaultOpen);
  const allOpen = openValues.length === stepValues.length;

  return (
    <>
      <div className="border-chariot-stroke flex items-center gap-2 border-b px-4 py-2.5">
        {!hideTitle && (
          <>
            <InfoIcon className="text-chariot-stroke-info size-4 shrink-0" />
            <h3 className="text-chariot-text-primary text-sm font-semibold">
              {title}
            </h3>
          </>
        )}
        <span className="text-chariot-text-secondary text-xs">
          {steps.length} {steps.length === 1 ? 'step' : 'steps'}
        </span>
        <button
          type="button"
          onClick={() => setOpenValues(allOpen ? [] : stepValues)}
          className="text-chariot-brand-primary hover:text-chariot-brand-primary focus-visible:ring-chariot-brand-primary ml-auto rounded-xs text-xs font-medium hover:underline focus-visible:ring-2 focus-visible:outline-hidden"
        >
          {allOpen ? 'Collapse all' : 'Expand all'}
        </button>
      </div>

      {intro.length > 0 ? (
        <div className="px-4 py-3">
          <BareElementGroup elements={intro} />
        </div>
      ) : null}

      <Accordion
        type="multiple"
        value={openValues}
        onValueChange={setOpenValues}
      >
        {steps.map((step, i) => (
          <ProofStepRow
            key={i}
            value={stepValues[i]}
            index={i}
            step={step}
            isFirst={i === 0}
          />
        ))}
      </Accordion>
    </>
  );
}

// ProofStepRow is one accordion row: a divider-separated trigger (chevron +
// crimson number badge + "Step N" eyebrow + title) over a panel of the step's
// elements. No bordered card per step.
function ProofStepRow({
  value,
  index,
  step,
  isFirst,
}: {
  value: string;
  index: number;
  step: ProofStep;
  isFirst: boolean;
}) {
  return (
    <AccordionItem
      value={value}
      className={cn('border-0', !isFirst && 'border-chariot-stroke border-t')}
    >
      <AccordionPrimitive.Header className="flex">
        <AccordionPrimitive.Trigger className="group focus-visible:ring-chariot-brand-primary flex min-h-12 w-full items-center gap-3 px-4 py-2.5 text-left focus-visible:ring-2 focus-visible:outline-hidden focus-visible:-outline-offset-2">
          <ChevronRight className="text-chariot-text-secondary size-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-90" />
          <span className="bg-chariot-brand-primary/10 text-chariot-brand-primary border-chariot-brand-primary flex size-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold">
            {index + 1}
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="text-chariot-text-secondary text-[10px] font-medium tracking-wider uppercase">
              Step {index + 1}
            </span>
            <span className="text-chariot-text-primary truncate text-sm font-medium group-data-[state=open]:whitespace-normal">
              {cleanStepTitle(step.title)}
            </span>
          </span>
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>
      <AccordionContent className="px-4 pt-0 pb-3 pl-13">
        <BareElementGroup elements={step.elements} />
      </AccordionContent>
    </AccordionItem>
  );
}

// ElementList groups elements by Heading element. Each Heading introduces
// a sub-card containing the elements that follow (until the next Heading).
// Elements before the first Heading render bare. This lets sections with
// multiple sub-areas render as a stack of visually-separated sub-cards
// without needing to fan them out into separate top-level Sections.
function ElementList({ elements }: { elements: ProofElement[] }) {
  type Group = { heading?: ProofHeading; elements: ProofElement[] };
  const groups: Group[] = [{ elements: [] }];
  for (const el of elements ?? []) {
    if (el.type === 'heading' && el.heading) {
      groups.push({ heading: el.heading, elements: [] });
    } else {
      groups[groups.length - 1].elements.push(el);
    }
  }

  return (
    <div className="space-y-3">
      {groups.map((g, i) => {
        if (g.elements.length === 0 && !g.heading) return null;
        if (g.heading) {
          return (
            <SubCardView key={i} title={g.heading.text} elements={g.elements} />
          );
        }
        return <BareElementGroup key={i} elements={g.elements} />;
      })}
    </div>
  );
}

// SubCardView wraps a heading-led group in a bordered card with a header
// strip — visually distinct from the surrounding section card.
function SubCardView({
  title,
  elements,
}: {
  title: string;
  elements: ProofElement[];
}) {
  return (
    <div className="border-chariot-stroke bg-chariot-background-secondary overflow-hidden rounded-md border">
      <div className="border-chariot-stroke border-b px-3 py-1.5">
        <h4 className="text-chariot-text-secondary text-xs font-medium tracking-wider uppercase">
          {title}
        </h4>
      </div>
      <div className="px-3 py-2">
        <BareElementGroup elements={elements} />
      </div>
    </div>
  );
}

// BareElementGroup renders elements with metric coalescing, no card chrome.
function BareElementGroup({ elements }: { elements: ProofElement[] }) {
  const out: React.ReactNode[] = [];
  let metricBuf: ProofMetric[] = [];
  const flushMetrics = () => {
    if (metricBuf.length === 0) return;
    out.push(
      <MetricGrid key={`m-${out.length}`} metrics={metricBuf.slice()} />
    );
    metricBuf = [];
  };

  (elements ?? []).forEach((el, idx) => {
    if (el.type === 'metric' && el.metric) {
      metricBuf.push(el.metric);
      return;
    }
    flushMetrics();
    out.push(<ElementView key={idx} element={el} />);
  });
  flushMetrics();

  return <div className="space-y-3">{out}</div>;
}

function ElementView({ element }: { element: ProofElement }) {
  switch (element.type) {
    case 'heading':
      return element.heading ? <HeadingView heading={element.heading} /> : null;
    case 'paragraph':
      return element.paragraph ? (
        <ParagraphView paragraph={element.paragraph} />
      ) : null;
    case 'group':
      return element.group ? <GroupView group={element.group} /> : null;
    case 'key_value':
      return element.key_value ? <KeyValueView kv={element.key_value} /> : null;
    case 'table':
      return element.table ? <TableView table={element.table} /> : null;
    case 'list':
      return element.list ? <ListView list={element.list} /> : null;
    case 'code_block':
      return element.code_block ? (
        <CodeBlockView code={element.code_block} />
      ) : null;
    case 'code_snippet':
      return element.code_snippet ? (
        <CodeSnippetView snippet={element.code_snippet} />
      ) : null;
    case 'http_exchange':
      return element.http_exchange ? (
        <HttpExchangeView exchange={element.http_exchange} />
      ) : null;
    case 'banner':
      return element.banner ? <BannerView banner={element.banner} /> : null;
    case 'metric':
      return element.metric ? <MetricGrid metrics={[element.metric]} /> : null;
    case 'reference':
      return element.reference ? (
        <ReferenceView reference={element.reference} />
      ) : null;
    case 'image':
      return element.image ? <ImageView image={element.image} /> : null;
    case 'timeline':
      return element.timeline ? (
        <TimelineView timeline={element.timeline} />
      ) : null;
    default:
      return null;
  }
}

// HeadingView matches the legacy sub-section heading style:
// `text-chariot-text-primary pb-1 font-medium` for clear-but-quiet titles.
function HeadingView({ heading }: { heading: ProofHeading }) {
  const level = Math.min(Math.max(heading.level || 2, 1), 3);
  const Tag = level === 1 ? 'h4' : level === 2 ? 'h5' : 'h6';
  return (
    <Tag className="text-chariot-text-primary pb-1 text-sm font-medium">
      {heading.text}
    </Tag>
  );
}

function ParagraphView({ paragraph }: { paragraph: ProofParagraph }) {
  if (paragraph.markdown) {
    return (
      <div className="text-chariot-text-primary text-sm">
        <MarkdownPreview source={paragraph.text} />
      </div>
    );
  }
  return (
    <p className="text-chariot-text-primary text-sm whitespace-pre-wrap">
      {paragraph.text}
    </p>
  );
}

function GroupView({ group }: { group: ProofGroup }) {
  return (
    <div className="border-chariot-stroke overflow-hidden rounded-lg border">
      {group.title ? (
        <div className="border-chariot-stroke bg-chariot-background-primary border-b px-4 py-3">
          <div className="text-chariot-text-primary text-sm font-medium">
            {group.title}
          </div>
        </div>
      ) : null}
      <div className="space-y-3 px-4 py-3">
        <ElementList elements={group.elements ?? []} />
      </div>
    </div>
  );
}

// KeyValueView renders rows as inline label-value pairs in a vertical
// `space-y-2` list — matching the legacy `<span className="text-chariot-brand-primary
// font-medium">{label}:</span><span className="ml-2">{value}</span>` pattern.
function KeyValueView({ kv }: { kv: ProofKeyValue }) {
  return (
    <div className="space-y-2">
      {(kv.rows ?? []).map((row, i) => (
        <KeyValueRowView key={i} row={row} />
      ))}
    </div>
  );
}

function KeyValueRowView({ row }: { row: ProofKeyValueRow }) {
  const valueClass = cn(
    'text-chariot-text-primary text-sm ml-2',
    row.severity && SEVERITY_TEXT[row.severity]
  );
  return (
    <div className="text-sm">
      <span className="text-chariot-brand-primary font-medium">{row.key}:</span>
      <span className={valueClass}>
        <ValueCell value={row.value} href={row.href} copyable={row.copyable} />
      </span>
    </div>
  );
}

function ValueCell({
  value,
  href,
  copyable,
}: {
  value: string;
  href?: string;
  copyable?: boolean;
}) {
  if (copyable) {
    // Copyable values render as a monospace span with inline copy icon —
    // matches the Detection Rule "Rule ID" treatment in legacy POE.
    return (
      <CopyToClipboard textToCopy={value} alwaysShowCopyIcon>
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="text-chariot-text-link font-mono text-xs break-all underline"
          >
            {value}
          </a>
        ) : (
          <span className="text-chariot-text-secondary font-mono text-xs">
            {value}
          </span>
        )}
      </CopyToClipboard>
    );
  }
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="text-chariot-text-link break-all underline"
      >
        {value}
      </a>
    );
  }
  return <span className="break-all">{value}</span>;
}

function TableView({ table }: { table: ProofTable }) {
  const columns = table.columns ?? [];
  const rows = table.rows ?? [];
  return (
    <div className="border-chariot-stroke overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-chariot-background-secondary">
          <tr>
            {columns.map(col => (
              <th
                key={col.key}
                className="text-chariot-text-secondary px-3 py-2 text-left text-xs font-medium tracking-wider uppercase"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className="border-chariot-stroke border-t last:border-b-0"
            >
              {columns.map(col => (
                <td
                  key={col.key}
                  className="text-chariot-text-primary px-3 py-2 align-top"
                >
                  {row[col.key] ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ListView matches the legacy "discovered secrets" pattern: bullet-dot +
// rounded background-secondary span, with optional copy + sublabel rules
// alongside.
function ListView({ list }: { list: ProofList }) {
  const items = list.items ?? [];
  if (list.style === 'numbered') {
    return (
      <ol className="text-chariot-text-primary list-decimal space-y-1 pl-5 text-sm">
        {items.map((item, i) => (
          <li key={i}>
            <ListItemText item={item} />
          </li>
        ))}
      </ol>
    );
  }
  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-3">
          {list.style === 'plain' ? null : (
            <span className="bg-chariot-stroke mt-1.5 flex size-2 shrink-0 items-center justify-center rounded-full" />
          )}
          <ListItemText item={item} />
        </div>
      ))}
    </div>
  );
}

function ListItemText({ item }: { item: ProofList['items'][number] }) {
  const labelNode = item.label ? (
    item.href ? (
      <a
        href={item.href}
        target="_blank"
        rel="noreferrer"
        className="text-chariot-text-link text-sm underline"
      >
        {item.label}
      </a>
    ) : (
      <span className="text-chariot-text-primary text-sm">{item.label}</span>
    )
  ) : null;

  const valueNode =
    item.value !== undefined ? (
      item.copyable ? (
        <CopyToClipboard textToCopy={item.value} alwaysShowCopyIcon>
          <span className="bg-chariot-background-secondary text-chariot-text-primary rounded-md px-2 py-1 font-mono text-xs">
            {item.value}
          </span>
        </CopyToClipboard>
      ) : (
        <span className="bg-chariot-background-secondary text-chariot-text-primary rounded-md px-2 py-1 font-mono text-xs">
          {item.value}
        </span>
      )
    ) : null;

  return (
    <span className="flex flex-wrap items-baseline gap-2">
      {labelNode}
      {valueNode}
      {item.sublabel ? (
        <span className="text-chariot-text-secondary text-xs">
          {item.sublabel}
        </span>
      ) : null}
    </span>
  );
}

const JSON_INDENT = '  ';

// Agent tool-call payloads arrive as a raw JSON string, so a `<pre>` shows the
// escaped wire form (`<`, `\"`, `\r\n`). We re-parse to decode those
// escapes and render with `formatJsonValue`, which (unlike JSON.stringify)
// expands multi-line string values — curl stdout, embedded HTML — into real
// line breaks instead of inline `\r\n`. Non-JSON content is left as-is.
// Recursion is bounded by JSON nesting depth; agent tool-call payloads are
// shallow, so no explicit depth guard is needed.
function prettyPrintIfJson(content: string): string {
  const trimmed = content.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return content;
  try {
    return formatJsonValue(JSON.parse(trimmed), 0);
  } catch {
    return content;
  }
}

function formatJsonValue(value: unknown, depth: number): string {
  if (typeof value === 'string') return formatJsonString(value, depth);
  if (value === null || typeof value !== 'object') return JSON.stringify(value);

  const pad = JSON_INDENT.repeat(depth);
  const padInner = JSON_INDENT.repeat(depth + 1);

  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    const items = value.map(v => padInner + formatJsonValue(v, depth + 1));
    return `[\n${items.join(',\n')}\n${pad}]`;
  }

  const entries = Object.entries(value);
  if (entries.length === 0) return '{}';
  const rows = entries.map(
    ([k, v]) =>
      `${padInner}${JSON.stringify(k)}: ${formatJsonValue(v, depth + 1)}`
  );
  return `{\n${rows.join(',\n')}\n${pad}}`;
}

// String values get three treatments, in order:
//   1. JSON-in-a-string (curl returns a JSON body as stdout) → recurse so it
//      renders as nested structure instead of one escaped line.
//   2. Multi-line text → quoted block, each line indented, so headers/HTML
//      read naturally instead of inline `\r\n`.
//   3. Plain single-line → JSON-quoted as-is.
function formatJsonString(value: string, depth: number): string {
  const trimmed = value.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      const nested = JSON.parse(trimmed);
      if (nested && typeof nested === 'object') {
        return formatJsonValue(nested, depth);
      }
    } catch {
      // not JSON — fall through to text handling
    }
  }

  // Treat bare CR as a line break too, so CR-only payloads still expand.
  const normalized = value.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  if (!normalized.includes('\n')) return JSON.stringify(value);

  const lines = normalized.split('\n');
  // A trailing newline (the norm for curl/HTTP stdout) leaves a final "" that
  // would render as a dangling blank line — drop it.
  if (lines.length > 1 && lines[lines.length - 1] === '') lines.pop();

  const padInner = JSON_INDENT.repeat(depth + 1);
  const body = lines.map(line => padInner + line).join('\n');
  return `"\n${body}\n${JSON_INDENT.repeat(depth)}"`;
}

// CollapsibleBlock is the shared chrome for the three full-output blocks
// (code_block / code_snippet / http_message). It owns the open/close state
// (default expanded so every block shows its full output on load) and renders:
//   • the bordered card frame,
//   • a clickable header `<button>` (rotating chevron + uppercase label +
//     optional right-aligned `headerRight` slot for the snippet language /
//     "(truncated)" badge),
//   • the body, which UNMOUNTS while collapsed (acceptable — default is open).
// The toggle is a real button with `aria-expanded` + `aria-controls`, is
// keyboard-operable, and carries the same `focus-visible` ring as the step
// trigger. The scroll-cap removal lives in each caller's body, not here.
function CollapsibleBlock({
  label,
  headerRight,
  children,
}: {
  label: string;
  headerRight?: React.ReactNode;
  children: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(true);
  const contentId = React.useId();

  return (
    <div className="border-chariot-stroke bg-chariot-background-secondary overflow-hidden rounded-md border">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen(o => !o)}
        className="focus-visible:ring-chariot-brand-primary border-chariot-stroke flex w-full items-center gap-2 border-b px-3 py-1 text-left focus-visible:ring-2 focus-visible:outline-hidden focus-visible:-outline-offset-2"
      >
        <ChevronRight
          className={cn(
            'text-chariot-text-secondary size-4 shrink-0 transition-transform duration-200',
            open && 'rotate-90'
          )}
        />
        <span className="text-chariot-text-secondary text-xs font-medium tracking-wider uppercase">
          {label}
        </span>
        {headerRight ? (
          <span className="ml-auto flex items-center">{headerRight}</span>
        ) : null}
      </button>
      {open ? <div id={contentId}>{children}</div> : null}
    </div>
  );
}

function CodeBlockView({ code }: { code: ProofCodeBlock }) {
  return (
    <CollapsibleBlock label={code.language || 'OUTPUT'}>
      <pre className="text-chariot-text-primary px-3 py-2 font-mono text-xs whitespace-pre-wrap">
        {prettyPrintIfJson(code.content)}
      </pre>
    </CollapsibleBlock>
  );
}

function CodeSnippetView({ snippet }: { snippet: ProofCodeSnippet }) {
  const lines = snippet.content.split('\n');
  if (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();

  const startLine = snippet.start_line ?? 1;

  const highlightAt: Record<number, 'match' | 'context'> = {};
  for (const h of snippet.highlights ?? []) {
    const from = Math.max(1, h.start_line);
    const to = Math.max(from, h.end_line ?? h.start_line);
    for (let l = from; l <= to; l++) {
      highlightAt[l] = h.kind ?? 'match';
    }
  }

  // Header label: file path when present (keeps the lowercase/mixed-case path
  // readable rather than uppercasing it), else the language (uppercased) or a
  // neutral `SNIPPET`. When a file path is the label, the language moves to the
  // right-aligned slot, preserving the original two-part header.
  const hasFilePath = Boolean(snippet.file_path);
  const label = hasFilePath
    ? snippet.file_path!
    : snippet.language
      ? snippet.language.toUpperCase()
      : 'SNIPPET';
  const headerRight =
    hasFilePath && snippet.language ? (
      <span className="text-chariot-text-secondary text-xs tracking-wider uppercase opacity-70">
        {snippet.language}
      </span>
    ) : undefined;

  return (
    <CollapsibleBlock label={label} headerRight={headerRight}>
      {/* Keep horizontal scroll for long `whitespace-pre` lines; the vertical
          `max-h-96` cap is intentionally gone so the full snippet renders. */}
      <div className="overflow-x-auto">
        <table className="w-full font-mono text-xs">
          <tbody>
            {lines.map((line, i) => {
              const lineNo = i + 1;
              const kind = highlightAt[lineNo];
              const isMatch = kind === 'match';
              return (
                <tr
                  key={i}
                  className={cn(isMatch && 'bg-chariot-status-critical/10')}
                >
                  <td className="text-chariot-text-secondary border-chariot-stroke w-12 border-r px-2 py-0.5 text-right select-none">
                    {startLine + i}
                  </td>
                  <td
                    className={cn(
                      'px-3 py-0.5 whitespace-pre',
                      isMatch
                        ? 'text-chariot-text-red'
                        : 'text-chariot-text-primary'
                    )}
                  >
                    {line || ' '}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </CollapsibleBlock>
  );
}

function HttpExchangeView({ exchange }: { exchange: ProofHttpExchange }) {
  return (
    <div className="space-y-3">
      {exchange.request ? (
        <HttpMessageView title="Request" message={exchange.request} />
      ) : null}
      {exchange.response ? (
        <HttpMessageView title="Response" message={exchange.response} />
      ) : null}
    </div>
  );
}

function HttpMessageView({
  title,
  message,
}: {
  title: string;
  message: NonNullable<ProofHttpExchange['request']>;
}) {
  const startLine =
    title === 'Request'
      ? `${message.method ?? ''} ${message.url ?? ''}`.trim()
      : `HTTP ${message.status_code ?? ''}`;
  const headerLines = (message.headers ?? []).map(h => `${h.name}: ${h.value}`);
  const body = message.body ?? '';
  const wireText = [startLine, ...headerLines, '', body]
    .filter((s, i) => i < 2 || s !== undefined)
    .join('\n');

  return (
    <CollapsibleBlock
      label={title}
      headerRight={
        message.truncated ? (
          <span className="text-chariot-text-yellow text-xs normal-case">
            (truncated)
          </span>
        ) : undefined
      }
    >
      <pre className="text-chariot-text-primary px-3 py-2 font-mono text-xs whitespace-pre-wrap">
        {wireText}
      </pre>
    </CollapsibleBlock>
  );
}

function BannerView({ banner }: { banner: ProofBanner }) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-md border-l-4 p-3',
        SEVERITY_BORDER[banner.severity],
        SEVERITY_BG[banner.severity]
      )}
    >
      <div className="flex-1">
        <div className="text-chariot-text-primary text-sm font-semibold">
          {banner.title}
        </div>
        {banner.body ? (
          <div className="text-chariot-text-secondary mt-1 text-xs whitespace-pre-wrap">
            {banner.body}
          </div>
        ) : null}
      </div>
      <span
        className={cn(
          'rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wider uppercase',
          SEVERITY_TEXT[banner.severity]
        )}
      >
        {banner.severity}
      </span>
    </div>
  );
}

function MetricGrid({ metrics }: { metrics: ProofMetric[] }) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      {metrics.map((m, i) => (
        <div
          key={i}
          className="border-chariot-stroke flex flex-col gap-1 rounded-md border p-3"
        >
          <div className="text-chariot-text-secondary text-xs font-medium tracking-wider uppercase">
            {m.label}
          </div>
          <div
            className={cn(
              'text-chariot-text-primary text-2xl font-semibold',
              m.severity && SEVERITY_TEXT[m.severity]
            )}
          >
            {m.value}
            {m.unit ? (
              <span className="text-chariot-text-secondary ml-1 text-sm font-normal">
                {m.unit}
              </span>
            ) : null}
          </div>
          {m.subtext ? (
            <div className="text-chariot-text-tertiary text-xs">
              {m.subtext}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function ReferenceView({ reference }: { reference: ProofReference }) {
  const url = reference.url || derivedReferenceURL(reference);
  const label = reference.label || reference.id || reference.kind;
  const tag = reference.kind.toUpperCase();

  return (
    <div className="text-sm">
      <span className="text-chariot-brand-primary font-medium">{tag}:</span>
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="text-chariot-text-link ml-2 underline"
        >
          {label}
        </a>
      ) : (
        <span className="text-chariot-text-primary ml-2">{label}</span>
      )}
    </div>
  );
}

function derivedReferenceURL(ref: ProofReference): string | undefined {
  if (!ref.id) return undefined;
  switch (ref.kind) {
    case 'cve':
      return `https://nvd.nist.gov/vuln/detail/${ref.id}`;
    case 'cwe': {
      const num = ref.id.replace(/^CWE-/i, '');
      return `https://cwe.mitre.org/data/definitions/${num}.html`;
    }
    case 'mitre':
      return `https://attack.mitre.org/techniques/${ref.id}`;
    default:
      return undefined;
  }
}

function ImageView({ image }: { image: ProofImage }) {
  // data_uri wins, then url (absolute URL used directly, storage key resolved
  // to a blob URL). See useResolvedImageSrc. data_uri / absolute-url images
  // render byte-identically to the previous `image.url || image.data_uri`.
  const { src, isLoading, isError } = useResolvedImageSrc(
    image.data_uri || image.url
  );

  // A storage-key fetch that is still in-flight renders a sized skeleton; one
  // that failed (or resolved empty) renders a fallback instead of vanishing.
  if (isLoading) {
    return (
      <div className="bg-chariot-background-secondary border-chariot-stroke aspect-video w-full animate-pulse rounded-md border" />
    );
  }
  if (isError) {
    return (
      <div className="bg-chariot-background-secondary border-chariot-stroke text-chariot-text-secondary flex aspect-video w-full items-center justify-center rounded-md border text-xs">
        Failed to load image
      </div>
    );
  }
  if (!src) return null;
  return (
    <figure className="border-chariot-stroke overflow-hidden rounded-md border">
      <img
        src={src}
        alt={image.alt_text ?? ''}
        className="block h-auto w-full"
      />
      {image.caption ? (
        <figcaption className="text-chariot-text-secondary border-chariot-stroke border-t px-3 py-2 text-xs">
          {image.caption}
        </figcaption>
      ) : null}
    </figure>
  );
}

// TimelineView mirrors the legacy "Recent Activity" visual: a vertical
// guide line running through circular nodes, each node holding a title
// (truncated at 80 chars with a tooltip showing the full text), an
// author/timestamp subtitle, and a copyable short-reference badge.
const MAX_TITLE = 80;

function TimelineView({ timeline }: { timeline: ProofTimeline }) {
  const entries = timeline.entries ?? [];
  if (entries.length === 0) return null;
  return (
    <div className="relative">
      <div className="bg-chariot-stroke-secondary absolute top-6 bottom-0 left-3 w-px" />
      <div className="space-y-4">
        {entries.map((entry, idx) => {
          const isLast = idx === entries.length - 1;
          const truncated =
            entry.title.length > MAX_TITLE
              ? `${entry.title.substring(0, MAX_TITLE)}…`
              : entry.title;
          return (
            <div key={idx} className="relative flex items-start space-x-4">
              <div className="relative z-10 flex size-6 items-center justify-center">
                <div className="border-chariot-background-primary bg-chariot-stroke size-3 rounded-full border-2" />
              </div>
              <div className="min-w-0 flex-1 pb-4">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    {entry.title.length > MAX_TITLE ? (
                      <InfoTooltip
                        title={
                          <div className="max-w-md whitespace-pre-wrap">
                            {entry.title}
                          </div>
                        }
                      >
                        <div className="text-chariot-text-primary cursor-help text-sm leading-5 font-medium">
                          {truncated}
                        </div>
                      </InfoTooltip>
                    ) : entry.href ? (
                      <a
                        href={entry.href}
                        target="_blank"
                        rel="noreferrer"
                        className="text-chariot-text-link text-sm leading-5 font-medium underline"
                      >
                        {truncated}
                      </a>
                    ) : (
                      <div className="text-chariot-text-primary text-sm leading-5 font-medium">
                        {truncated}
                      </div>
                    )}
                    {entry.subtitle ? (
                      <div className="text-chariot-text-tertiary mt-1 text-xs">
                        {entry.subtitle}
                      </div>
                    ) : null}
                  </div>
                  {entry.reference ? (
                    <div className="ml-3 shrink-0">
                      <CopyToClipboard
                        iconClassName="size-3 text-chariot-text-secondary"
                        alwaysShowCopyIcon
                        textToCopy={entry.reference}
                      >
                        <code className="border-chariot-stroke text-chariot-text-secondary rounded border px-2 py-1 font-mono text-xs whitespace-nowrap">
                          {entry.reference}
                        </code>
                      </CopyToClipboard>
                    </div>
                  ) : null}
                </div>
              </div>
              {isLast ? (
                <div className="bg-chariot-background-primary absolute top-6 bottom-0 left-3 w-px" />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
